/*
 * A BOUNDED KEY/VALUE STORE ON INDEXEDDB — paint from cache, reconcile from the source.
 *
 * The pattern is Linear's sync engine and it is the one thing that removes the last visible wait in
 * an app: a surface draws what it already knew, instantly, and the live read replaces it a moment
 * later. Two places need it — the app's structural snapshots (rosters, controller descriptors) and
 * the AI panel's transcripts — and they were one implementation and none respectively.
 *
 * WHAT THIS ADDS OVER THE ONE IT REPLACES IS THE BOUND.
 *
 * The original had a 7-day TTL and an org-scoped boot sweep and no size limit of any kind: one entry
 * per controller, each as large as that controller's program, growing until the browser's quota
 * refused a write — at which point every subsequent write failed silently and the cache quietly
 * stopped working while looking like it worked. A cache without a ceiling is not a cache, it is a
 * leak with a read path.
 *
 * So there is a byte cap and an LRU, and the LRU needs a fact the original did not record: `at` is
 * when an entry was WRITTEN, and eviction has to know when it was last USED. A cache evicting by
 * write time throws away the thing you open every morning in favour of the thing written most
 * recently and never read again.
 *
 * EVERY FAILURE IS SURVIVABLE. Private mode, a disabled store, a quota refusal, a blocked upgrade —
 * all resolve to "no cache" rather than throwing. The callers' fallback is the network, which is
 * what they did before this existed.
 */
/**
 * A cache. Constructed rather than global so two of them can exist with different budgets — a
 * transcript is worth more room than a roster, and one shared ceiling would let whichever wrote last
 * evict the other.
 */
export class IdbStore {
    constructor(opts) {
        this.opts = opts;
        this.dbPromise = null;
        this.pending = new Map();
        /**
         * The index, in memory: what is stored, how big, and when it was last touched.
         *
         * Held here so eviction is a decision made against numbers rather than a scan of the store on
         * every write. Built once, lazily, from one read-only pass — a cost paid on the first write of a
         * session rather than on the first read, so nothing a reader waits for is behind it.
         */
        this.index = null;
        this.indexing = null;
    }
    open() {
        if (this.dbPromise)
            return this.dbPromise;
        this.dbPromise = new Promise((resolve) => {
            try {
                if (typeof indexedDB === 'undefined')
                    return resolve(null);
                const req = indexedDB.open(this.opts.db, 1);
                req.onupgradeneeded = () => {
                    if (!req.result.objectStoreNames.contains(STORE))
                        req.result.createObjectStore(STORE);
                };
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => resolve(null);
                // A blocked upgrade means another tab holds an older version open. Waiting would hang the
                // caller; there is no cache today and there will be one on the next load.
                req.onblocked = () => resolve(null);
            }
            catch {
                resolve(null);
            }
        });
        return this.dbPromise;
    }
    /** Read one entry, honouring the age limit and marking it as used. */
    async get(key) {
        const db = await this.open();
        if (!db)
            return null;
        try {
            const e = (await promisify(tx(db, 'readonly').get(key)));
            if (!e)
                return null;
            if (Date.now() - e.at > this.opts.maxAgeMs) {
                // Dropped on sight rather than left for a sweep: a stale entry that is being ASKED for is
                // exactly the one worth the delete, and leaving it means the next read pays the same check.
                void this.delete([key]);
                return null;
            }
            /*
             * THE TOUCH IS WHAT MAKES IT AN LRU, and it is deliberately fire-and-forget.
             *
             * A reader must not wait on a write to learn what it already has in hand. The worst outcome
             * of a lost touch is that a hot entry looks colder than it is and is evicted a little early —
             * which costs one refetch, against making every cache hit slower than it needs to be.
             */
            const used = Date.now();
            const idx = this.index?.get(key);
            if (idx)
                idx.used = used;
            void promisify(tx(db, 'readwrite').put({ ...e, used }, key)).catch(() => undefined);
            return e.data;
        }
        catch {
            return null;
        }
    }
    /** Write one entry, debounced per key, and make room for it if it does not fit. */
    put(key, data) {
        const ms = this.opts.debounceMs ?? 0;
        if (!ms) {
            void this.write(key, data);
            return;
        }
        clearTimeout(this.pending.get(key));
        this.pending.set(key, setTimeout(() => {
            this.pending.delete(key);
            void this.write(key, data);
        }, ms));
    }
    async write(key, data) {
        const db = await this.open();
        if (!db)
            return;
        try {
            const bytes = sizeOf(data);
            /*
             * A VALUE THAT CANNOT FIT IS NOT WRITTEN, rather than written and then immediately evicting
             * everything else to make room for itself. A single enormous transcript would otherwise empty
             * the cache on its way in and then be evicted by the next write — all cost, no benefit.
             */
            if (bytes > this.opts.maxBytes)
                return;
            const now = Date.now();
            await promisify(tx(db, 'readwrite').put({ at: now, used: now, bytes, data }, key));
            const idx = await this.ensureIndex(db);
            idx.set(key, { used: now, bytes });
            await this.evict(db, idx);
        }
        catch {
            /* quota, private mode, a store that vanished — the cache stays best-effort. */
        }
    }
    /** Forget these keys. Used by callers that know something is gone (a gateway that left the org). */
    async delete(keys) {
        if (!keys.length)
            return;
        const db = await this.open();
        if (!db)
            return;
        try {
            const store = tx(db, 'readwrite');
            // Issued together, then awaited together. Awaiting between deletes lets the transaction
            // auto-close on an empty microtask turn, so everything after the first silently does nothing.
            await Promise.all(keys.map((k) => promisify(store.delete(k))));
            for (const k of keys)
                this.index?.delete(k);
        }
        catch {
            /* best-effort */
        }
    }
    /** Forget everything whose key does not start with one of these prefixes, plus anything aged out.
     *  The org-scoping sweep: keys are prefixed by org, so switching org needs no cleanup hook. */
    async sweep(keepPrefixes) {
        const db = await this.open();
        if (!db)
            return;
        try {
            const now = Date.now();
            const entries = await this.scan(db);
            const dead = entries
                .filter((e) => !keepPrefixes.some((p) => e.key.startsWith(p)) || now - e.at > this.opts.maxAgeMs)
                .map((e) => e.key);
            await this.delete(dead);
        }
        catch {
            /* best-effort */
        }
    }
    /** Every key currently stored. For a caller that reconciles against something it owns — which
     *  keys are dead is that caller's question, not this store's. */
    async keys() {
        const db = await this.open();
        if (!db)
            return [];
        try {
            return (await promisify(tx(db, 'readonly').getAllKeys()));
        }
        catch {
            return [];
        }
    }
    /** Every key with its bookkeeping. ONE transaction, read in one go — the read-then-await-per-key
     *  shape auto-closes the transaction and silently returns nothing after the first row. */
    async scan(db) {
        const store = tx(db, 'readonly');
        const [keys, values] = await Promise.all([promisify(store.getAllKeys()), promisify(store.getAll())]);
        const ks = keys;
        const vs = values;
        return ks.map((key, i) => ({
            key,
            at: vs[i]?.at ?? 0,
            // An entry written before `used` existed has never been read as far as we know, which is the
            // right thing to assume: its write time is the only evidence there is.
            used: vs[i]?.used ?? vs[i]?.at ?? 0,
            bytes: vs[i]?.bytes ?? 0,
        }));
    }
    async ensureIndex(db) {
        if (this.index)
            return this.index;
        this.indexing ??= (async () => {
            const rows = await this.scan(db).catch(() => []);
            this.index = new Map(rows.map((r) => [r.key, { used: r.used, bytes: r.bytes }]));
        })();
        await this.indexing;
        return (this.index ??= new Map());
    }
    /**
     * Make room, oldest-USED first.
     *
     * By last read rather than last write, which is the whole reason `used` is a separate field: a
     * cache evicting by write time discards the thread you open every morning in favour of one written
     * five minutes ago and never looked at again.
     */
    async evict(db, idx) {
        let total = 0;
        for (const v of idx.values())
            total += v.bytes;
        if (total <= this.opts.maxBytes)
            return;
        const cold = [...idx.entries()].sort((a, b) => a[1].used - b[1].used);
        const drop = [];
        for (const [key, v] of cold) {
            if (total <= this.opts.maxBytes)
                break;
            drop.push(key);
            total -= v.bytes;
        }
        await this.delete(drop);
    }
}
const STORE = 'entries';
const tx = (db, mode) => db.transaction(STORE, mode).objectStore(STORE);
const promisify = (req) => new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
});
/**
 * Roughly how much room a value takes.
 *
 * Serialized length, in UTF-16 code units doubled — an estimate, because a structured clone is not
 * JSON and the browser's real accounting is not observable. It only has to be CONSISTENT: a budget
 * measured the same way every time bounds the store whether or not the number matches what the disk
 * reports, and no correct answer is available at any price.
 */
export function sizeOf(data) {
    try {
        return JSON.stringify(data)?.length * 2 || 0;
    }
    catch {
        // A cycle or a BigInt. Unknown size is treated as large, so an unmeasurable value does not slip
        // past the budget by being unmeasurable.
        return Number.MAX_SAFE_INTEGER;
    }
}
//# sourceMappingURL=idb-store.js.map